/**
 * Data and commands of the fuel cell endpoints of `AVS_Web-API` (`Tls/FuelCell/...`).
 * Mirrors the models in `APISharedLib` (Entities/TLS/FuelcellCommandsModels.cs,
 * Entities/WebApi/Fuelcell.cs) and `WebApi.Models.TLS.Response.Fuelcell*DataModel`.
 * Enums are transferred as numbers.
 */

import { SchemaDeviceInfo } from 'src/app/project/project-schema/shared/interfaces/schema-route-station-info.interface';
import { BrennstoffzellenDetails } from './brennstoffzellen-details';

/**
 * Addressing of the fuel cell channel (`RouteStationIdChannelModel`): used for all read and
 * command endpoints and delivered back by every read endpoint.
 */
export interface FuelcellChannelAddress {
    /** Id of the route station */
    id: string;
    /** DE channel of the fuel cell */
    deChannel: number;
}

/** GET Tls/FuelCell/Details (`FuelcellDetails`): latest system state message (DE type 139) and device data (DE type 254) */
export interface FuelcellDetails {
    statusData?: BrennstoffzellenDetails | null;
    deviceInfo?: SchemaDeviceInfo | null;
}

/** GET/PUT Tls/FuelCell/IgnoreExternalFuelSensor (FG06 settings of the fuel cell channel, database only) */
export interface FuelcellSettings extends FuelcellChannelAddress {
    /** null: the setting is not available for the device */
    ignoreExternalFuelSensor?: boolean | null;
}

/** GET Tls/FuelCell/SystemState (DE type 139) */
export interface FuelcellSystemStateData extends FuelcellChannelAddress {
    systemState?: number | null;
    /** 0 = automatic, 1 = off, 255 = unknown */
    operatingMode?: number | null;
    manualSwitchOnPossible?: boolean | null;
    manualSwitchOffPossible?: boolean | null;
    fuelManagerError?: boolean | null;
    time?: string | null;
}

/** Requested system state change (FG6 type 196), APISharedLib `SystemStateChangeActionType` */
export enum SystemStateChangeActionType {
    None = 0,
    RemoveTransportLock = 1,
    On = 2,
    Off = 3,
    Auto = 4,
    Reset = 255
}

export enum CartridgeType {
    Unspecified = 0,
    Custom = 1,
    M5 = 2,
    M10 = 3,
    M28 = 4,
    MT60 = 5
}

export const cartridgeTypeLabels: { type: CartridgeType, label: string }[] = [
    { type: CartridgeType.M5, label: 'M5' },
    { type: CartridgeType.M10, label: 'M10' },
    { type: CartridgeType.M28, label: 'M28' },
    { type: CartridgeType.MT60, label: 'MT60' },
    { type: CartridgeType.Custom, label: 'Benutzerdefiniert' }
];

export interface CartridgeConfig {
    cType: CartridgeType;
    /** Capacity [l], required only for type Custom */
    capacity?: number | null;
}

export interface CartridgeConfigPort {
    /** 0 with only one cartridge (without fuel manager), otherwise counting from 1 */
    portNumber: number;
    cartridgeConfig: CartridgeConfig;
}

export interface FuelcellFillLevel {
    portNumber: number;
    level?: number | null;
}

/** Fill level to send, POST Tls/FuelCell/ChangeFillLevel */
export interface FuelcellPortLevel {
    portNumber: number;
    level: number;
}

/** GET Tls/FuelCell/CartridgeData (DE types 138 and 142) */
export interface FuelcellCartridgeData extends FuelcellChannelAddress {
    activePort?: number | null;
    fillLevels: FuelcellFillLevel[];
    time?: string | null;
    cartridgesConfig?: CartridgeConfigPort[] | null;
    cartridgesConfigTime?: string | null;
}

export enum BatteryType {
    NoBattery = 0,
    LeadAcid_12V = 1,
    LeadAcid_24V = 2,
    LiFePO4_12V = 3,
    LiFePO4_24V = 4,
    EFOY = 5
}

export const batteryTypeLabels: { type: BatteryType, label: string }[] = [
    { type: BatteryType.NoBattery, label: 'Keine Batterie' },
    { type: BatteryType.LeadAcid_12V, label: 'Blei 12 V' },
    { type: BatteryType.LeadAcid_24V, label: 'Blei 24 V' },
    { type: BatteryType.LiFePO4_12V, label: 'LiFePO4 12 V' },
    { type: BatteryType.LiFePO4_24V, label: 'LiFePO4 24 V' },
    { type: BatteryType.EFOY, label: 'EFOY' }
];

export enum BatteryLoadConfigDataType {
    Undefined = 0,
    BeginOfCharge = 1,
    EndOfChargeValue = 2,
    SwitchOffCurrentValue = 3,
    CapacityValue = 4,
    SwitchOffTimeValue = 5,
    MaxChargeTimeValue = 6,
    MinChargeTimeValue = 7,
    SwitchOnDelayValue = 8,
    DeepDischargeProtectionVoltageValue = 9,
    MaxStateOfChargeValue = 10,
    MaxDepthOfDischargeValue = 11,
    DeepDischargeProtectionStateOfChargeValue = 12
}

export function getBatteryLoadConfigDataTypeString(type: BatteryLoadConfigDataType): string {
    switch (type) {
        case BatteryLoadConfigDataType.BeginOfCharge:
            return 'Ladebeginn';
        case BatteryLoadConfigDataType.EndOfChargeValue:
            return 'Ladeende';
        case BatteryLoadConfigDataType.SwitchOffCurrentValue:
            return 'Ausschaltstrom';
        case BatteryLoadConfigDataType.CapacityValue:
            return 'Kapazität';
        case BatteryLoadConfigDataType.SwitchOffTimeValue:
            return 'Ausschaltzeit';
        case BatteryLoadConfigDataType.MaxChargeTimeValue:
            return 'Max. Ladedauer';
        case BatteryLoadConfigDataType.MinChargeTimeValue:
            return 'Min. Ladedauer';
        case BatteryLoadConfigDataType.SwitchOnDelayValue:
            return 'Einschaltverzögerung';
        case BatteryLoadConfigDataType.DeepDischargeProtectionVoltageValue:
            return 'Tiefentladeschutz Spannung';
        case BatteryLoadConfigDataType.MaxStateOfChargeValue:
            return 'Max. Ladezustand (SOC)';
        case BatteryLoadConfigDataType.MaxDepthOfDischargeValue:
            return 'Max. Entladetiefe (DoD)';
        case BatteryLoadConfigDataType.DeepDischargeProtectionStateOfChargeValue:
            return 'Tiefentladeschutz SOC';
        default:
            return 'Unbekannt (' + type + ')';
    }
}

/** Unit of a load parameter, empty for an unknown type */
export function getBatteryLoadConfigDataTypeUnit(type: BatteryLoadConfigDataType): string {
    switch (type) {
        case BatteryLoadConfigDataType.BeginOfCharge:
        case BatteryLoadConfigDataType.EndOfChargeValue:
        case BatteryLoadConfigDataType.DeepDischargeProtectionVoltageValue:
            return 'V';
        case BatteryLoadConfigDataType.SwitchOffCurrentValue:
            return 'A';
        case BatteryLoadConfigDataType.CapacityValue:
            return 'Ah';
        case BatteryLoadConfigDataType.SwitchOffTimeValue:
        case BatteryLoadConfigDataType.MinChargeTimeValue:
            return 'min';
        case BatteryLoadConfigDataType.MaxChargeTimeValue:
            return 'h';
        case BatteryLoadConfigDataType.SwitchOnDelayValue:
            return 's';
        case BatteryLoadConfigDataType.MaxStateOfChargeValue:
        case BatteryLoadConfigDataType.MaxDepthOfDischargeValue:
        case BatteryLoadConfigDataType.DeepDischargeProtectionStateOfChargeValue:
            return '%';
        default:
            return '';
    }
}

export interface BatteryConfigDataSet {
    val: number;
    minVal: number;
    maxVal: number;
    defVal: number;
}

export interface BatteryConfigDataSetComplete {
    type: BatteryLoadConfigDataType;
    data?: BatteryConfigDataSet | null;
}

export interface BatteryConfigListData {
    batType: BatteryType;
    configData: BatteryConfigDataSetComplete[];
}

/** GET Tls/FuelCell/BatteryConfigData (DE type 143) */
export interface FuelcellBatteryConfigData extends FuelcellChannelAddress {
    batteryConfig?: BatteryConfigListData | null;
    time?: string | null;
}

export interface BatteryLoadConfigValue {
    configDataType: BatteryLoadConfigDataType;
    configDataValue: number;
}
